import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Tab } from 'semantic-ui-react';

import { log } from '../../../../../services/utils';

import {
	getParticipantsList,
	unenrollParticipant
} from '../../../../../api/nora/programs';

import { useAuthState } from '../../../../../contexts/AuthContext';
import {
	useProgramAction,
	useProgramState
} from '../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Actions from '../../../../Shared/Actions';
import Alert from '../../../../Alert';
import HelpModal from '../../../../Shared/HelpModal';
import PageHeader from '../../../../Shared/PageHeader';
import ParticipantsModal from './ParticipantsModal';
import Table from '../../../../Table';
import TableRowActions from '../../../../Table/Cells/TableRowActions';

const ParticipantsTab = ( { activeIndex } ) => {
	const history = useHistory();

	const { user: { permissions } } = useAuthState();
	const { program } = useProgramState();
	const { setParticipants } = useProgramAction();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ participantsModal, setParticipantsModal ] = useState( false );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ participantToDelete, setParticipantToDelete ] = useState( {} );

	const retrieveParticipantsList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Participants List...' );

		try {
			const data = await getParticipantsList( program?.id );

			setParticipants( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		program,
		setParticipants,
		setApiErrorAlert
	] );

	const closeParticipantsModal = useCallback( () => {
		setParticipantsModal( false );
	}, [] );

	const onView = useCallback( ( { id } ) => {
		history.push( {
			pathname: `/agencies/directory/${ id }`,
			state: {
				text: '\u2190\u00A0\u00A0Back to Program',
				to: {
					pathname: `/operations/programs/${ program?.id }`,
					state: { activeIndex }
				}
			}
		} );
	}, [
		activeIndex,
		history,
		program
	] );

	const onDelete = useCallback( participant => {
		setParticipantToDelete( participant );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewAgencies.canExecute,
				icon: 'search',
				permission: permissions.viewAgencies.isAvailable,
				text: 'View Agency'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.editPrograms.canExecute,
				icon: 'times',
				permission: permissions.editPrograms.isAvailable,
				text: 'Remove Agency'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onView,
		onDelete,
		permissions
	] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Agency Name',
			filter: 'text',
			width: 240,
			minWidth: 60
		},
		{
			id: 'location',
			accessor: 'location',
			Header: 'Location',
			minWidth: 60
		}
	], [] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewAgencies.isAvailable || permissions.editPrograms.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const addAgency = useCallback( () => {
		setParticipantsModal( true );
	}, [] );

	const agencyActions = useMemo( () => [
		{
			action: addAgency,
			disable: !permissions.editPrograms.canExecute,
			icon: 'add',
			permission: permissions.editPrograms.isAvailable,
			text: 'Add Agency'
		}
	], [
		addAgency,
		permissions
	] );

	const handleRemoveParticipant = useCallback( async () => {
		setInfobarLoader( 'Retrieving Participants List...' );

		try {
			await unenrollParticipant( program?.id, participantToDelete.id );

			await retrieveParticipantsList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		participantToDelete.id,
		program,
		retrieveParticipantsList,
		setApiErrorAlert,
		setInfobarLoader
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to remove <strong>{ participantToDelete.agencyName }</strong> from the list?</span>, [ participantToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	return (
		<Tab.Pane as="div">
			{ log( 'participantsTab' ) }
			<Actions actions={ [] } />
			<PageHeader
				actions={ agencyActions }
				title={
					<>
						<span>Participating Agencies</span>
						<HelpModal
							id={ 10 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			{ program.participants?.length
				? <Table
					columns={ renderColumns }
					data={ program.participants }
					showCount={ program.participants.length > 10 }
					showEmptyRows={ false }
					showPagination={ program.participants.length > 10 }
				/>
				: <p>No participants.</p>
			}
			<ParticipantsModal
				closeModal={ closeParticipantsModal }
				open={ participantsModal }
				participants={ program.participants }
				retrieveParticipantsList={ retrieveParticipantsList }
			/>
			<Alert
				action={ handleRemoveParticipant }
				name="remove-participant-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</Tab.Pane>
	);
};

ParticipantsTab.propTypes = { activeIndex: PropTypes.number };

export default memo( ParticipantsTab );
